import React, { useEffect, useState } from 'react';
import cuid from 'cuid';
import EditOrderApi from './EditOrderApi';
import { Translate } from '../../../../shared/LanguageService';
import AddedProducts from './components/AddedProducts';
import { IChangeStatus, IOrderRow, IProduct } from './Types';
import AdditionalProductSelection from './components/AdditionalProductSelection';
import { ParseTranslations, TranslatedLabels } from '../../../../shared/types';

export enum OrderRowChangeStatus {
    Unknown = 0,
    ChangedByPhotographer = 1,
    AddedByPhotographer = 2,
    RemovedByPhotographer = 3
}

export type ChangeStatus = {
    text: string;
    status: OrderRowChangeStatus;
};

export type AdditionalProduct = {
    id: string;
    productId: number;
    name: string;
};

export type IAdditionalProductButton = {
    id: string;
    productId: number;
    name: string;
    disabled: boolean;
};

export type ExchangeableProduct = {
    id: string;
    productId: number;
    name: string;
    isAnExtraProduct: boolean;
};

export type AddedProduct = {
    id: string;
    orderRowId: number | null;
    productId: number;
    name: string;
    isRemovable: boolean;
    removed: boolean;
    isExchangeable: boolean;
    changeStatus: ChangeStatus;
    exchangeableProducts: ExchangeableProduct[];
};

export type AddedOrderRow = {
    shouldHydrate: true;
    orderId: number;
    productId: number;
    productName: string;
    productPhotographerCompensationSetManually: false;
    productPriceBrokerFirm: -1;
    productPricePropertyOwner: -1;
    quantity: -1;
    invoiceComment: null;
    photographerComment: null;
    removed: false;
};

type EditOrderPageProps = {
    labels: TranslatedLabels;
    parseTranslations: ParseTranslations;
    addedProducts: AddedProduct[];
    availableAdditionalProducts: AdditionalProduct[];
    orderId: number;
};

type TranslatedEditOrderPageProps = {
    orderId: number;
};

type EditOrderPageContainerProps = {
    orderId: number;
    labels: TranslatedLabels;
    parseTranslations: ParseTranslations;
};

function transformAdditionalProductToAddedProduct(
    additionalProduct: AdditionalProduct,
    changeStatusText: string,
    exchangeableProducts: ExchangeableProduct[]
): AddedProduct {
    return {
        id: additionalProduct.id,
        productId: additionalProduct.productId,
        orderRowId: null,
        name: additionalProduct.name,
        isRemovable: true,
        removed: false,
        isExchangeable: true,
        exchangeableProducts,
        changeStatus: {
            status: OrderRowChangeStatus.AddedByPhotographer,
            text: changeStatusText
        }
    };
}

function transformProductToAddedOrderRow(orderId: number, product: AddedProduct): AddedOrderRow {
    return {
        shouldHydrate: true,
        orderId,
        productId: product.productId,
        productName: product.name,
        productPriceBrokerFirm: -1,
        productPricePropertyOwner: -1,
        productPhotographerCompensationSetManually: false,
        quantity: -1,
        invoiceComment: null,
        photographerComment: null,
        removed: false
    };
}

function transformExchangeableProductToAddedProduct(
    orderRowId: number,
    product: ExchangeableProduct,
    exchangeableProducts: ExchangeableProduct[],
    isRemovable: boolean,
    removed: boolean,
    isExchangeable: boolean,
    labels: TranslatedLabels
): AddedProduct {
    return {
        id: product.id,
        productId: product.productId,
        name: product.name,
        exchangeableProducts,
        orderRowId,
        isRemovable,
        removed,
        isExchangeable,
        changeStatus: {
            status: OrderRowChangeStatus.ChangedByPhotographer,
            text: labels.changeStatus__changeByPhotographer
        }
    };
}

function isProductSupposedToBeDisabled(product: AddedProduct): boolean {
    if (!product) {
        return false;
    }

    if (!product.changeStatus) {
        return true;
    }

    return product.changeStatus.status !== OrderRowChangeStatus.RemovedByPhotographer;
}

function transformAdditionalProductToAdditionalProductButtonFactory(products: AddedProduct[]) {
    return function transformAdditionalProductToAdditionalProductButton(
        additionalProduct: AdditionalProduct
    ): IAdditionalProductButton {
        const productAlreadyAdded = products.find(
            (product) => product.productId === additionalProduct.productId
        );

        return {
            id: cuid(),
            productId: additionalProduct.productId,
            name: additionalProduct.name,
            disabled: isProductSupposedToBeDisabled(productAlreadyAdded)
        };
    };
}

function transformAdditionalProductToExchangeableProduct(
    additionalProduct: AdditionalProduct
): ExchangeableProduct {
    return {
        id: cuid(),
        productId: additionalProduct.productId,
        name: additionalProduct.name,
        isAnExtraProduct: true
    };
}

export function EditOrderPage({
    addedProducts,
    availableAdditionalProducts,
    labels,
    parseTranslations,
    orderId
}: EditOrderPageProps) {
    const [products, setProducts] = useState<AddedProduct[]>(addedProducts);
    const [newProduct, setNewProduct] = useState<AddedProduct | null>(null);
    const [productIdForRemoval, setProductIdForRemoval] = useState<string | null>(null);
    const [showAddExtraProductConfirmationModal, setShowAddExtraProductConfirmationModal] =
        useState<boolean>(false);
    const [showRemoveExtraProductConfirmationModal, setShowRemoveExtraProductConfirmationModal] =
        useState<boolean>(false);

    function handleAddExtraProduct(extraProduct: AdditionalProduct) {
        const newProductToAdd = transformAdditionalProductToAddedProduct(
            extraProduct,
            labels.changeStatus__addedByPhotographer,
            availableAdditionalProducts.map(transformAdditionalProductToExchangeableProduct)
        );
        setNewProduct(newProductToAdd);
        setShowAddExtraProductConfirmationModal(true);
    }

    function handleCancelAddExtraProduct() {
        setShowAddExtraProductConfirmationModal(false);
    }

    function handleConfirmAddExtraProduct() {
        if (newProduct) {
            setProducts((currentProducts) => currentProducts.concat([newProduct]));

            const addedOrderRow = transformProductToAddedOrderRow(orderId, newProduct);
            EditOrderApi.createOrderRow(orderId, addedOrderRow)
                .then((response) => {
                    const { id, productId } = response.orderRow;
                    setProducts((currentProducts) =>
                        currentProducts.map((product) => {
                            if (product.productId === productId && product.orderRowId === null) {
                                return {
                                    ...product,
                                    orderRowId: id
                                };
                            }

                            return product;
                        })
                    );
                })
                .catch((error) => {
                    // TODO: ApplicationInsights logException
                });
        }

        setShowAddExtraProductConfirmationModal(false);
    }

    function handleCloseAddExtraProductConfirmationModal() {
        setShowAddExtraProductConfirmationModal(false);
    }

    function handleRemoveExtraProduct(id: string) {
        setProductIdForRemoval(id);
        setShowRemoveExtraProductConfirmationModal(true);
    }

    function handleConfirmRemoveExtraProduct() {
        setProducts((currentProducts) =>
            currentProducts.map((currentProduct) => {
                if (currentProduct.id === productIdForRemoval) {
                    EditOrderApi.removeOrderRow(orderId, currentProduct.orderRowId).catch(
                        (error) => {
                            // TODO: ApplicationInsights logException
                        }
                    );
                    return {
                        ...currentProduct,
                        changeStatus: {
                            status: OrderRowChangeStatus.RemovedByPhotographer,
                            text: labels.changeStatus__removedByPhotographer
                        }
                    };
                }
                return currentProduct;
            })
        );
        setShowRemoveExtraProductConfirmationModal(false);
    }

    function handleCancelRemoveExtraProduct() {
        setShowRemoveExtraProductConfirmationModal(false);
    }

    function handleChangeAddedProductFactory(orderRowId: number) {
        return function handleChangeAddedProduct(newProduct: ExchangeableProduct) {
            setProducts((currentProducts) =>
                currentProducts.map((currentProduct) => {
                    if (currentProduct.orderRowId === orderRowId) {
                        const product = transformExchangeableProductToAddedProduct(
                            orderRowId,
                            newProduct,
                            currentProduct.exchangeableProducts,
                            currentProduct.isRemovable,
                            currentProduct.removed,
                            currentProduct.isExchangeable,
                            labels
                        );
                        return product;
                    }
                    return currentProduct;
                })
            );

            EditOrderApi.updateOrderRowProduct(orderId, orderRowId, newProduct.productId).catch(
                (error) => {
                    // TODO: ApplicationInsights logException
                }
            );
        };
    }

    const transformAdditionalProductToAdditionalProductButton =
        transformAdditionalProductToAdditionalProductButtonFactory(products);

    return (
        <div className="editPhotoOrder__container">
            <div className="editPhotoOrder__titleContainer">
                <h2 className="editPhotoOrder__title">{labels.title}</h2>
            </div>
            <div className="editPhotoOrder__content">
                <AddedProducts
                    products={products}
                    onRemoveExtraProduct={handleRemoveExtraProduct}
                    labels={labels}
                    parseTranslations={parseTranslations}
                    onChangeAddedProductFactory={handleChangeAddedProductFactory}
                    showRemoveExtraProductConfirmationModal={
                        showRemoveExtraProductConfirmationModal
                    }
                    onConfirmRemoveExtraProductConfirmationModal={handleConfirmRemoveExtraProduct}
                    onCancelRemoveExtraProductConfirmationModal={handleCancelRemoveExtraProduct}
                    onCloseRemoveExtraProductConfirmationModal={handleCancelRemoveExtraProduct}
                />
                <AdditionalProductSelection
                    availableAdditionalProducts={availableAdditionalProducts.map(
                        transformAdditionalProductToAdditionalProductButton
                    )}
                    onAddExtraProduct={handleAddExtraProduct}
                    labels={labels}
                    showAddExtraProductConfirmationModal={showAddExtraProductConfirmationModal}
                    onConfirmAddExtraProductConfirmationModal={handleConfirmAddExtraProduct}
                    onCancelAddExtraProductConfirmationModal={handleCancelAddExtraProduct}
                    onCloseAddExtraProductConfirmationModal={
                        handleCloseAddExtraProductConfirmationModal
                    }
                />
            </div>
        </div>
    );
}

function getChangeStatusText(changeStatus: number, labels: TranslatedLabels): string {
    if (changeStatus === OrderRowChangeStatus.AddedByPhotographer) {
        return labels.changeStatus__addedByPhotographer;
    }

    if (changeStatus === OrderRowChangeStatus.ChangedByPhotographer) {
        return labels.changeStatus__changeByPhotographer;
    }

    if (changeStatus === OrderRowChangeStatus.RemovedByPhotographer) {
        return labels.changeStatus__removedByPhotographer;
    }

    return '';
}

function transformAvailableProductToExchangeableProduct(
    availableProduct: IProduct
): ExchangeableProduct {
    return {
        id: cuid(),
        productId: availableProduct.id,
        name: availableProduct.name,
        isAnExtraProduct: availableProduct.productTypeId === 3
    };
}

function transformChangeStatus(
    changeStatus: IChangeStatus,
    labels: TranslatedLabels
): ChangeStatus | null {
    if (changeStatus.value === OrderRowChangeStatus.Unknown) {
        return null;
    }

    return {
        text: getChangeStatusText(changeStatus.value, labels),
        status: changeStatus.value
    };
}

function removedProduct(orderRow: IOrderRow): boolean {
    if (
        orderRow.changeStatus &&
        orderRow.changeStatus.value === OrderRowChangeStatus.RemovedByPhotographer
    ) {
        return false;
    }
    return true;
}

function getAddedProducts(orderRows: IOrderRow[], labels: TranslatedLabels): AddedProduct[] {
    return orderRows.filter(removedProduct).map((orderRow) => ({
        id: cuid(),
        productId: orderRow.productId,
        orderRowId: orderRow.id,
        name: orderRow.productName,
        isRemovable: orderRow.canRemove,
        removed: orderRow.removed,
        isExchangeable: orderRow.canChange,
        changeStatus: orderRow.changeStatus
            ? transformChangeStatus(orderRow.changeStatus, labels)
            : null,
        exchangeableProducts: orderRow.availableProducts.map(
            transformAvailableProductToExchangeableProduct
        )
    }));
}

function generateUniqueId(availableProduct: IProduct): AdditionalProduct {
    return {
        productId: availableProduct.id,
        id: cuid(),
        name: availableProduct.name
    };
}

function EditOrderPageContainer({
    orderId,
    labels,
    parseTranslations
}: EditOrderPageContainerProps) {
    const [data, setData] = useState(null);

    useEffect(() => {
        EditOrderApi.view(orderId)
            .then((result) => {
                setData(result);
            })
            .catch((error) => {
                // TODO: ApplicationInsights logException
                // TODO: Visualize that the request failed, "try again"
            });
    }, [orderId]);

    if (!data) {
        // TODO: Proper loading indicator
        return null;
    }

    const { customer, orderRows } = data;
    
    const addedProducts = getAddedProducts(orderRows, labels);

    const availableAdditionalProducts =
        customer && customer.possibleProducts
            ? customer.possibleProducts.map(generateUniqueId)
            : [];

    return (
        <EditOrderPage
            orderId={orderId}
            addedProducts={addedProducts}
            availableAdditionalProducts={availableAdditionalProducts}
            labels={labels}
            parseTranslations={parseTranslations}
        />
    );
}

function TranslatedEditOrderPage({ orderId }: TranslatedEditOrderPageProps) {
    const labels: TranslatedLabels = {
        title: Translate('view-order/basic-order-info/panel-headers/products'),
        productColumnTitle: Translate('view-order/basic-order-info/products/table-headers/name'),
        editButtonLabel: Translate('view-order/basic-order-info/products/button-labels/edit'),
        removeButtonLabel: Translate('delete/button-label/delete'),
        addAdditionalProduct__title: Translate(
            'view-order/photographer-edit/products/selector/label'
        ),
        addAdditionalProduct__placeholder: Translate(
            'view-order/photographer-edit/products/selector/placeholder'
        ),
        addAdditionalProduct__addButtonLabel: Translate(
            'view-order/basic-order-info/products/button-labels/add'
        ),
        addAdditionalProduct__confirmationModal__title: Translate(
            'view-order/photographer-edit/modal/add-order-row/title'
        ),
        addAdditionalProduct__confirmationModal__text: Translate(
            'view-order/photographer-edit/modal/add-order-row/text'
        ),
        addAdditionalProduct__confirmationModal__cancelButtonLabel: Translate(
            'view-order/photographer-edit/modal/add-order-row/button-labels/cancel'
        ),
        addAdditionalProduct__confirmationModal__confirmButtonLabel: Translate(
            'view-order/photographer-edit/modal/add-order-row/button-labels/do'
        ),
        changeOrderRow__confirmationModal__title: Translate(
            'view-order/photographer-edit/modal/edit-order-row/title'
        ),

        changeOrderRow__confirmationModal__cancelButtonLabel: Translate(
            'view-order/photographer-edit/modal/edit-order-row/button-labels/cancel'
        ),
        changeOrderRow__confirmationModal__confirmButtonLabel: Translate(
            'view-order/photographer-edit/modal/edit-order-row/button-labels/do'
        ),
        changeStatus__changeByPhotographer: Translate(
            'order-row-change-status/change-by-photographer'
        ),
        changeStatus__removedByPhotographer: Translate(
            'order-row-change-status/removed-by-photographer'
        ),
        changeStatus__addedByPhotographer: Translate(
            'order-row-change-status/added-by-photographer'
        ),
        changeStatus__unknown: Translate('order-row-change-status/unknown'),
        removeOrderRow__confirmationModal__title: Translate('delete/modal/title/delete'),
        removeOrderRow__confirmationModal__text: Translate(
            'view-order/photographer-edit/modal/remove-order-row/text'
        ),
        removeOrderRow__confirmationModal__confirmButtonLabel: Translate(
            'delete/modal/footer/button-label/delete'
        ),
        removeOrderRow__confirmationModal__cancelButtonLabel: Translate(
            'delete/modal/footer/button-label/cancel'
        )
    };

    const parseTranslations: ParseTranslations = {
        changeOrderRow__confirmationModal__text: (productName: string) =>
            Translate('view-order/photographer-edit/modal/edit-order-row/text-parse', {
                newProduct: productName
            })
    };

    return (
        <EditOrderPageContainer
            orderId={orderId}
            labels={labels}
            parseTranslations={parseTranslations}
        />
    );
}

export default TranslatedEditOrderPage;
